// Le CV adapté et la lettre arrivent sous les yeux (chantier « Mouvement », #184).
//
// Les documents vivent dans une iframe sandboxée SANS scripts : l'animation
// s'y pose depuis le parent (accès par allow-same-origin), en CSS seul, comme
// la pagination. Deux scènes :
// - la vue des changements : chaque passage ajouté ou reformulé s'allume à
//   son tour, puis garde sa couleur (l'image finale est le surlignage normal) ;
// - la lettre : chaque paragraphe se pose, un léger flou qui se dissipe.
// Le CV éditable ne bouge jamais : on écrit dedans.
//
// UNE FOIS PAR CONTENU : une lettre régénérée (nouveau texte) rejoue, revenir
// sur la même lettre ou rouvrir la vue des changements ne rejoue pas. Mémoire
// de la page, pas du stockage : à la session suivante, ce document n'est de
// toute façon plus « nouveau » qu'au premier regard.

import { MOUVEMENT, mouvementReduit } from './mouvement'

// Bornée comme memoireDesVus : Radar reste ouvert longtemps.
const joues: string[] = []
const MAX_JOUES = 200

/** Empreinte courte d'un texte (djb2) : on ne garde pas le document en mémoire. */
function empreinte(s: string): string {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
  return `${s.length}:${h >>> 0}`
}

/** Au plus autant d'éléments animés : au-delà, la cascade durerait trop. */
const MAX_ELEMENTS = 36

// Écran seulement : le PDF est l'impression de cette iframe, et une page
// imprimée pendant la cascade sortirait des paragraphes invisibles.
const STYLE = `
@media screen {
@keyframes mvAllume { from { background-color: transparent; box-shadow: none; } }
@keyframes mvPose { from { opacity: 0; transform: translateY(6px); filter: blur(2px); } }
.mv-changements .mv-c {
  animation: mvAllume ${MOUVEMENT.arrivee * 2}ms ease-out both;
  animation-delay: calc(${MOUVEMENT.arrivee}ms + var(--mv-k, 0) * 110ms);
}
.mv-lettre .mv-p {
  animation: mvPose ${MOUVEMENT.arrivee * 1.5}ms ease-out both;
  animation-delay: calc(150ms + var(--mv-k, 0) * 300ms);
}
}
`

/**
 * Pose l'animation sur un document qui vient de se charger dans l'aperçu.
 * Rend vrai si elle joue. `contenu` est le TEXTE lu par la personne (pas le
 * HTML : une police ou un modèle changés ne font pas un nouveau document).
 */
export function animerDocument(doc: Document, kind: 'cv' | 'letter' | 'diff', contenu: string): boolean {
  if (kind === 'cv' || mouvementReduit()) return false
  // Une iframe cachée (fenêtre repliée, onglet hors écran) ne joue rien et ne
  // note rien : on ne grille pas l'animation sur un document que personne ne voit.
  const cadre = doc.defaultView?.frameElement as HTMLElement | null | undefined
  if (cadre && cadre.getClientRects().length === 0) return false
  const cle = `${kind}:${empreinte(contenu)}`
  if (joues.includes(cle)) return false
  const cibles = kind === 'diff'
    ? [...doc.querySelectorAll<HTMLElement>('.diff-add, .diff-reword')]
    : [...doc.body.querySelectorAll<HTMLElement>('p')].filter((p) => (p.textContent ?? '').trim().length > 0)
  if (cibles.length === 0) return false
  joues.push(cle)
  if (joues.length > MAX_JOUES) joues.splice(0, joues.length - MAX_JOUES)
  if (!doc.getElementById('mv-style')) {
    const style = doc.createElement('style')
    style.id = 'mv-style'
    style.textContent = STYLE
    doc.head.appendChild(style)
  }
  cibles.slice(0, MAX_ELEMENTS).forEach((el, k) => {
    el.style.setProperty('--mv-k', String(k))
    // Une classe posée seulement sur les éléments de la cascade : au-delà de
    // MAX_ELEMENTS, rien ne s'allume en avance, en même temps que le premier.
    el.classList.add(kind === 'letter' ? 'mv-p' : 'mv-c')
  })
  doc.documentElement.classList.add(kind === 'diff' ? 'mv-changements' : 'mv-lettre')
  return true
}

/** Pour les tests : oublie ce qui a déjà joué. */
export function oublierDocumentsJoues(): void {
  joues.length = 0
}
